import React, { useState, useEffect } from 'react';
import { Check, GripVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Card,
  Snackbar,
  Popover,
  Alert,
  Box,
  CardContent,
  FormControlLabel,
  Switch,
  MenuItem,
  FormGroup,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import WhatsappApiFunctions from '../API/whatsapp';
import EditTimingDialog from './EditTimingModal';

const URL = process.env.REACT_APP_API_ENDPOINT;

const WICLAX_CHANNEL_OPTIONS = [
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

const normalizeWiclaxTimeField = (value) => (String(value || '').toLowerCase() === 'time' ? 'time' : 'chiptime');

const FilterContext = React.createContext();

const FilterProvider = ({ children }) => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  return (
    <FilterContext.Provider value={{ selectedMonth, setSelectedMonth, selectedYear, setSelectedYear }}>
      {children}
    </FilterContext.Provider>
  );
};

export default function EventPage() {
  const [open, setOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    document.title = 'Event Management - Marathon Tracker';
  }, []);

  // Function to trigger refresh
  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    async function getTempleList() {
      // This API call it to send the user to the login page on access token error
      try {
        const response = await WhatsappApiFunctions.getTempleList();
        console.log('response ', response);
      } catch (error) {
        console.error('Error fetching message count:', error);
      }
    }
    getTempleList();
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">Event Management</h1>
          <div className="flex items-center space-x-4">
            <ListEventFilters />
            <CreateEventInfo open={open} setOpen={setOpen} onEventCreated={handleRefresh} />
          </div>
        </div>

        <ListEventInfo refreshKey={refreshKey} />
      </main>
    </div>
  );
}

const ListEventInfo = ({ refreshKey }) => {
  const [events, setEvents] = useState([]);
  const token = localStorage.getItem('accessToken');
  const [loading, setLoading] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [editRefreshKey, setEditRefreshKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [completeEventId, setCompleteEventId] = useState(null);
  const [finalContent, setFinalContent] = useState('');

  const handleManageClick = (event) => {
    navigate(`/dashboard/runners/${event.id}`, { 
      state: { 
        eventName: event.name, 
        isRekla: event.IsRekala,
        startTime: event.startTime,
        endTime: event.endTime
      } 
    });
  };

  const handleOpenEditDialog = (event) => {
    setEditEvent(event);
    setEditOpen(true);
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        console.log('Fetching events with token:', token);
        const queryParams = new URLSearchParams({
          page: '1',
          limit: '15',
          ...(selectedMonth && { month: selectedMonth }),
          ...(selectedYear && { year: selectedYear }),
        });

        const res = await fetch(`${URL}Event/ListEventInfo?${queryParams}`, {
          headers: {
            accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.status === 403) {
          console.warn('Access forbidden: 403');
          localStorage.setItem('adminAuthenticated', false);
          navigate('/login');
          return;
        }
        const data = await res.json();

        if (data.Status === 1 && Array.isArray(data.Events)) {
          const mappedEvents = data.Events.map((ev) => ({
            id: ev.EventInfoKey,
            name: ev.EventName,
            startTime: ev.StartTime,
            endTime: ev.EndTime,
            IsCompleted: ev.IsCompleted,
            logoUrl: ev.LogoUrl,
            LiveTrackStatus: ev.LiveTrackStatus,
            IsRekala: ev.IsRekala,
            IsWiclax: ev.IsWiclax,
            WiclaxResultsUrl: ev.WiclaxResultsUrl,
            WiclaxChannels: ev.WiclaxChannels,
            WiclaxTimeField: ev.WiclaxTimeField,
            status: determineEventStatus(ev.StartTime, ev.EndTime),
            timingLaps: (ev.time_slabs || []).map((ts) => ({
              TimingId: ts.TimingId,
              TimingName: ts.TimingName,
              DisplayOrder: ts.DisplayOrder,
              TimingSlabKey: ts.TimingSlabKey,
            })),
          }));

          setEvents(mappedEvents);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [refreshKey, editRefreshKey, selectedMonth, selectedYear]);

  const determineEventStatus = (start, end) => {
    const now = new Date().getTime();
    const startTime = new Date(start).getTime();
    const endTime = new Date(end).getTime();

    if (now < startTime) return 'not_started';
    if (now >= startTime && now <= endTime) return 'in_progress';
    return 'completed';
  };

  const handleMarkAsComplete = async () => {
    if (!completeEventId || !finalContent) return;

    const token = localStorage.getItem('accessToken');

    const formBody = new URLSearchParams();
    formBody.append('EventInfoKey', completeEventId);
    formBody.append('FinalContent', finalContent);

    try {
      const res = await fetch(`${URL}Event/CompleteEvent`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
        body: formBody.toString(),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error('Failed to complete event:', data);
        toast.error(`Failed: ${data.message || res.statusText}`);
        return;
      }

      toast.success('Event marked as completed!');
      setCompleteModalOpen(false);
      setFinalContent('');
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setEditRefreshKey((prev) => prev + 1);
    }
  };

  return (
    <>
      <div className="mb-6 flex gap-4 items-center justify-end">
        <Button
          variant="outlined"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          endIcon={<FilterListIcon />}
          sx={{
            color: '#9f1d21',
            borderColor: '#9f1d21',
            '&:hover': {
              borderColor: '#e3272c',
              backgroundColor: '#fff0f5',
            },
          }}
        >
          Filters {selectedMonth || selectedYear ? '(Active)' : ''}
        </Button>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, width: 300 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Filters
            </Typography>
            <div className="flex flex-col gap-4">
              <TextField
                size="small"
                select
                label="Month"
                value={selectedMonth || ''}
                onChange={(e) => setSelectedMonth(e.target.value || null)}
                fullWidth
              >
                <MenuItem value="">All Months</MenuItem>
                {[...Array(12)].map((_, index) => (
                  <MenuItem key={index + 1} value={index + 1}>
                    {new Date(2000, index).toLocaleString('default', { month: 'long' })}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Year"
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(e.target.value || null)}
                size="small"
                fullWidth
              >
                <MenuItem value="">All Years</MenuItem>
                {[...Array(10)].map((_, index) => {
                  const year = new Date().getFullYear() - 5 + index;
                  return (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  );
                })}
              </TextField>
              <div className="flex justify-between mt-2">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedMonth(null);
                    setSelectedYear(null);
                    setAnchorEl(null);
                  }}
                  sx={{
                    color: '#9f1d21',
                    borderColor: '#9f1d21',
                    '&:hover': {
                      borderColor: '#e3272c',
                      backgroundColor: '#fff0f5',
                    },
                  }}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setAnchorEl(null)}
                  sx={{
                    backgroundColor: '#9f1d21',
                    '&:hover': {
                      backgroundColor: '#e3272c',
                    },
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          </Box>
        </Popover>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="bg-gradient-to-r from-blue-400 to-purple-500 rounded-full w-12 h-12 animate-pulse" />
        </div>
      ) : events.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-2">
          {events?.map((event) => (
            <div
              key={event.id}
              className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="p-4 border-b border-gray-200">
                <div className="flex justify-between">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <h2 className="text-xl font-semibold">{cleanAndCapitalize(event.name)}</h2>
                    <span
                      className={`inline-block px-2 py-1 text-xs rounded-full ${
                        event.IsCompleted === 1 ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {event.IsCompleted === 1 ? 'Completed' : 'In Progress'}
                    </span>
                    <span
                      style={{
                        backgroundColor: 'hsl(270, 60%, 92%)',
                        color: 'hsl(270, 60%, 28%)',
                        display: event?.IsRekala === 1 ? 'block' : 'none',
                      }}
                      className="inline-block px-2 py-1 text-xs rounded-full bg-purple-500 text-purple-800"
                    >
                      Rekla
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    {/* event.status !== "completed" */}
                    {!event.IsCompleted && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-green-600 hover:text-green-700"
                        onClick={() => {
                          setCompleteEventId(event.id);
                          setCompleteModalOpen(true);
                        }}
                      >
                        <Check className="mr-1 h-4 w-4" />
                        Complete
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 hover:text-blue-700"
                      onClick={() => handleOpenEditDialog(event)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
                <p style={{ fontSize: 12 }}>#{event?.id}</p>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Start Time</p>
                    <p>
                      {event?.startTime &&
                        new Date(event.startTime)
                          .toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })
                          .replace(/\//g, '-')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">End Time</p>
                    <p>
                      {event?.endTime &&
                        new Date(event.endTime)
                          .toLocaleString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })
                          .replace(/\//g, '-')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4 mt-4">
                  {/* Left Side: Timing Laps (80%) */}
                  <div className="w-5/8">
                    <p className="text-sm text-gray-500 mb-1">Timing Laps</p>
                    {event.timingLaps.length > 0 ? (
                      <div className="flex flex-wrap gap-1 text-sm">
                        {event.timingLaps.map((lap, index) => (
                          <span key={index} className="inline-block bg-gray-100 rounded px-2 py-1">
                            {lap.TimingName}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No timing data</p>
                    )}
                  </div>

                  {/* Right Side: Manage Runners Button (20%) */}
                  <div className="w-3/8 flex justify-end items-center">
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleManageClick(event)}
                      sx={{
                        backgroundColor: '#9f1d21',
                        '&:hover': {
                          backgroundColor: '#e3272c',
                        },
                        color: '#fff',
                      }}
                    >
                      Manage Runners
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20">
          <p className="text-xl mb-4">No events found</p>
          <p className="text-gray-600 mb-6">Get started by creating your first event</p>
          <Button onClick={() => setOpen(true)} className="bg-[#9f1d21] hover:bg-blue-700">
            Create New Event
          </Button>
        </div>
      )}
      <EditEventInfo
        event={editEvent}
        open={editOpen}
        setOpen={setEditOpen}
        onEventUpdated={() => setEditRefreshKey((prev) => prev + 1)}
      />

      <Dialog
        open={completeModalOpen}
        onClose={() => setCompleteModalOpen(false)}
        maxWidth="md" // Options: 'xs', 'sm', 'md', 'lg', 'xl', false
      >
        <DialogTitle>Mark Event as Complete</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={finalContent}
            onChange={(e) => setFinalContent(e.target.value)}
            placeholder="Enter thank you message or summary..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteModalOpen(false)} sx={{ color: '#9f1d21' }}>
            Cancel
          </Button>
          <Button
            onClick={handleMarkAsComplete}
            variant="contained"
            sx={{ backgroundColor: '#9f1d21', '&:hover': { backgroundColor: '#e3272c' } }}
          >
            Mark as Complete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CreateEventInfo = ({ open, setOpen, onEventCreated }) => {
  const token = localStorage.getItem('accessToken');
  const [lapName, setLapName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lapTimingPoint, setLapTimingPoint] = useState('');
  const [newEvent, setNewEvent] = useState({
    name: '',
    startTime: '',
    endTime: '',
    timingDetails: '',
    logo: '',
    status: 'not_started',
    timingLaps: [],
  });
  const [uploadProgress, setUploadProgress] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [liveTrackStatus, setLiveTrackStatus] = useState(true);
  const [isRekla, setIsRekla] = useState(false);
  const [isWiclax, setIsWiclax] = useState(false);
  const [wiclaxResultsUrl, setWiclaxResultsUrl] = useState('');
  const [wiclaxChannels, setWiclaxChannels] = useState([]);
  const [wiclaxTimeField, setWiclaxTimeField] = useState('chiptime');

  const handleSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEvent((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddLap = () => {
    if (!lapName) {
      handleSnackbar('Please fill in all lap fields', 'error');
      return;
    }

    const newLap = {
      id: newEvent.timingLaps.length.toString(), // use index as ID
      TimingId: lapName,
      TimingName: lapTimingPoint,
    };

    setNewEvent((prev) => ({
      ...prev,
      timingLaps: [...prev.timingLaps, newLap],
    }));

    setLapName('');
    setLapTimingPoint('');
  };

  const handleReklaChange = (value) => {
    console.log('value: ', value);
    setIsRekla(value);

    if (value) {
      setIsWiclax(false);
      setLiveTrackStatus(false);
      const startLap = {
        id: '0', // use index as ID
        TimingId: 11,
        TimingName: 'START',
      };

      const finishLap = {
        id: '1', // use next index as ID
        TimingId: 12,
        TimingName: 'FINISH',
      };

      setNewEvent((prev) => ({
        ...prev,
        timingLaps: [startLap, finishLap], // Replace all existing laps with only START and FINISH
      }));

      setLapName('');
      setLapTimingPoint('');
    } else {
      setNewEvent((prev) => ({
        ...prev,
        timingLaps: prev.timingLaps.filter((lap) => lap.TimingId !== 11 && lap.TimingId !== 12),
      }));
    }
  };

  const handleWiclaxChange = (value) => {
    setIsWiclax(value);

    if (value) {
      setIsRekla(false);
      setWiclaxTimeField('chiptime');
    } else {
      setWiclaxResultsUrl('');
      setWiclaxChannels([]);
      setWiclaxTimeField('chiptime');
    }
  };

  const handleWiclaxChannelToggle = (channel) => {
    setWiclaxChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]
    );
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();

    if (!newEvent.name || !newEvent.startTime || !newEvent.endTime) {
      handleSnackbar('Please fill in all required fields', 'error');
      return;
    }

    if (isWiclax && (!wiclaxResultsUrl || wiclaxChannels.length === 0)) {
      handleSnackbar('Please enter the Wiclax Results Url and select at least one Wiclax Channel', 'error');
      return;
    }

    setIsLoading(true); // Start loading
    console.log('new event: ', newEvent);
    const apiBody = {
      EventName: newEvent.name,
      StartTime: newEvent.startTime,
      EndTime: newEvent.endTime,
      LogoUrl: newEvent.logo || '',
      LiveTrackStatus: liveTrackStatus ? 1 : 0,
      IsRekala: isRekla ? 1 : 0,
      time_slabs: newEvent.timingLaps.map((lap, index) => ({
        TimingId: Number(lap.TimingId),
        TimeSlabName: lap.TimingName,
        DisplayOrder: index,
      })),
      IsWiclax: isWiclax ? 1 : 0,
      WiclaxEventName: isWiclax ? newEvent.name : '',
      WiclaxResultsUrl: isWiclax ? wiclaxResultsUrl : '',
      WiclaxChannels: isWiclax ? wiclaxChannels.join(',') : '',
      WiclaxTimeField: isWiclax ? wiclaxTimeField : '',
    };

    try {
      const res = await fetch(`${URL}Event/CreateEventInfo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        body: JSON.stringify(apiBody),
      });

      // 🔒 Handle 403 explicitly
      if (res.status === 403) {
        toast.error('Session expired. Please login again.');
        localStorage.setItem('adminAuthenticated', false);
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return;
      }

      const data = await res.json();

      if (!res.ok) {
        handleSnackbar(`Failed: ${data.message || res.statusText}`, 'error');
        return;
      }

      handleSnackbar('Event created successfully!');
      setNewEvent({
        name: '',
        startTime: '',
        endTime: '',
        timingDetails: '',
        logo: '',
        status: 'not_started',
        timingLaps: [],
      });
      setOpen(false);
      onEventCreated?.(); // optional chaining to avoid errors if undefined
    } catch (error) {
      console.error(error);
      handleSnackbar('Something went wrong. Try again.', 'error');
    } finally {
      setIsRekla(false);
      setIsWiclax(false);
      setWiclaxResultsUrl('');
      setWiclaxChannels([]);
      setWiclaxTimeField('chiptime');
      setIsLoading(false); // Stop loading
    }
  };

  const handleReorderLaps = (reorderedLaps) => {
    setNewEvent((prev) => ({ ...prev, timingLaps: reorderedLaps }));
  };

  const handleDeleteLap = (id) => {
    setNewEvent((prev) => ({
      ...prev,
      timingLaps: prev.timingLaps.filter((lap) => lap.TimingId !== id),
    }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('LogoImage', file);
    setUploadProgress(true);
    try {
      const response = await fetch(`${URL}Event/uploadEventImage`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
          // Do NOT set Content-Type manually when using FormData with fetch
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      if (data.Status) {
        setNewEvent((prev) => ({ ...prev, logo: data.Url }));
      }
    } catch (error) {
      console.error('Image upload failed:', error);
    } finally {
      setUploadProgress(false);
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          sx={{ backgroundColor: '#9f1d21', '&:hover': { backgroundColor: '#e3272c' } }}
        >
          Create New Event
        </Button>
      </Box>

      {/* <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth> */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm">
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Create New Event
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleCreateEvent}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="name"
                  label="Event Name"
                  value={newEvent.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid item xs={6}>
                  <DateTimePicker
                    label="Start Time"
                    value={newEvent.startTime ? new Date(newEvent.startTime) : null}
                    onChange={(newValue) =>
                      handleInputChange({
                        target: {
                          name: 'startTime',
                          value: newValue ? format(newValue, "yyyy-MM-dd'T'HH:mm:ss") : '',
                        },
                      })
                    }
                    ampm
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                  />
                </Grid>

                <Grid item xs={6}>
                  <DateTimePicker
                    label="End Time"
                    value={newEvent.endTime ? new Date(newEvent.endTime) : null}
                    onChange={(newValue) =>
                      handleInputChange({
                        target: {
                          name: 'endTime',
                          value: newValue ? format(newValue, "yyyy-MM-dd'T'HH:mm:ss") : '',
                        },
                      })
                    }
                    ampm
                    renderInput={(params) => <TextField {...params} fullWidth required />}
                  />
                </Grid>
              </LocalizationProvider>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={liveTrackStatus}
                      onChange={(e) => setLiveTrackStatus(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Live Track Status"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isRekla}
                      disabled={isWiclax}
                      onChange={(e) => {
                        console.log(e.target.checked, 'asdasd');
                        handleReklaChange(e.target.checked);
                      }}
                      color="primary"
                    />
                  }
                  label="Is Rekla"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isWiclax}
                      disabled={isRekla}
                      onChange={(e) => handleWiclaxChange(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Is Wiclax"
                />
              </Grid>

              {isWiclax && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Wiclax Results Url"
                      value={wiclaxResultsUrl}
                      onChange={(e) => setWiclaxResultsUrl(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">Wiclax Channels</Typography>
                    <FormGroup row>
                      {WICLAX_CHANNEL_OPTIONS.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          control={
                            <Checkbox
                              checked={wiclaxChannels.includes(option.value)}
                              onChange={() => handleWiclaxChannelToggle(option.value)}
                            />
                          }
                          label={option.label}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                  <Grid item xs={12}>
                    <FormLabel component="legend">Wiclax Timing</FormLabel>
                    <RadioGroup
                      row
                      value={wiclaxTimeField}
                      onChange={(e) => setWiclaxTimeField(e.target.value)}
                    >
                      <FormControlLabel value="chiptime" control={<Radio />} label="Chip Time" />
                      <FormControlLabel value="time" control={<Radio />} label="Timing" />
                    </RadioGroup>
                  </Grid>
                </>
              )}

              <Grid item xs={12} sx={isRekla ? { display: 'none' } : {}}>
                <Typography variant="subtitle1">Event Logo</Typography>

                <Button
                  variant="outlined"
                  component="label"
                  sx={{
                    mt: 1,
                    mr: 2,
                    color: '#9f1d21',
                    borderColor: '#9f1d21',
                    '&:hover': {
                      borderColor: '#9f1d21',
                      backgroundColor: 'rgba(240, 78, 152, 0.08)', // optional hover effect
                    },
                  }}
                  disabled={uploadProgress}
                >
                  Upload Logo
                  <input type="file" accept="image/*" hidden onChange={handleLogoUpload} />
                </Button>

                {newEvent.logo && (
                  <>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setNewEvent((prev) => ({ ...prev, logo: '' }))}
                      sx={{ mt: 1 }}
                    >
                      Remove Logo
                    </Button>

                    <Box mt={2}>
                      <img src={newEvent.logo} alt="Event Logo" style={{ height: 100 }} />
                    </Box>
                  </>
                )}
              </Grid>

              {/* Timing Laps */}
              <Grid item xs={12} sx={isRekla ? { display: 'none' } : {}}>
                <Typography variant="subtitle1">Timing Laps</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      placeholder="Mat ID"
                      value={lapName}
                      onChange={(e) => setLapName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      placeholder="Timing Point Name"
                      value={lapTimingPoint}
                      onChange={(e) => setLapTimingPoint(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      disabled={isLoading}
                      startIcon={<AddIcon />}
                      onClick={handleAddLap}
                      sx={{
                        backgroundColor: '#ffff',
                        color: '#9f1d21',
                        borderColor: '#9f1d21',
                        '&:hover': {
                          backgroundColor: '#ffff',
                          borderColor: '#e3272c',
                        },
                      }}
                    >
                      Add Lap
                    </Button>
                  </Grid>
                </Grid>
              </Grid>

              {/* Drag-and-drop laps section (placeholder) */}
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  (Drag and reorder laps here)
                </Typography>
                {/* Replace this with your actual drag-and-drop component */}
                <div className="pt-4">
                  <DragAndMoveExample
                    isRekla={isRekla}
                    timingLaps={newEvent.timingLaps || []}
                    onReorder={(reorderedLaps) => handleReorderLaps(reorderedLaps, false)}
                    onDelete={handleDeleteLap}
                    isCreate
                  />
                </div>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setOpen(false)}
              disabled={isLoading}
              variant="outlined"
              sx={{
                color: '#9f1d21',
                borderColor: '#9f1d21',
                '&:hover': {
                  borderColor: '#e3272c',
                  backgroundColor: '#fff0f5', // light pink background on hover
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                backgroundColor: '#9f1d21',
                '&:hover': {
                  backgroundColor: '#e3272c',
                },
              }}
            >
              Create Event
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const EditEventInfo = ({ open, setOpen, event, onEventUpdated }) => {
  const token = localStorage.getItem('accessToken');
  const [editEvent, setEditEvent] = useState(event);
  const [editLapName, setEditLapName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(false);
  const [editLapTimingPoint, setEditLapTimingPoint] = useState('');
  const [liveTrackStatus, setLiveTrackStatus] = useState(true);
  const [isRekla, setIsRekla] = useState(false);
  const [isPreviousValueIsRekla, setIsPreviousValueIsRekla] = useState(false);
  const [showReklaConfirmDialog, setShowReklaConfirmDialog] = useState(false);
  const [isWiclax, setIsWiclax] = useState(false);
  const [wiclaxResultsUrl, setWiclaxResultsUrl] = useState('');
  const [wiclaxChannels, setWiclaxChannels] = useState([]);
  const [wiclaxTimeField, setWiclaxTimeField] = useState('chiptime');

  useEffect(() => {
    setEditEvent(event);
    setLiveTrackStatus(event?.LiveTrackStatus === 1);
    setIsWiclax(event?.IsWiclax === 1 || event?.IsWiclax === true || event?.IsWiclax === '1');
    setWiclaxResultsUrl(event?.WiclaxResultsUrl || '');
    setWiclaxChannels(event?.WiclaxChannels ? event.WiclaxChannels.split(',').filter(Boolean) : []);
    setWiclaxTimeField(normalizeWiclaxTimeField(event?.WiclaxTimeField));
    setIsRekla(event?.IsRekala === 1);
    setIsPreviousValueIsRekla(event?.IsRekala === 1);
  }, [event]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditEvent((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddLap = () => {
    if (!editLapName || !editLapTimingPoint) {
      toast.error('Please fill in all lap fields');
      return;
    }

    const newLap = {
      id: editEvent.timingLaps.length.toString(),
      TimingId: editLapName,
      TimingName: editLapTimingPoint,
      isNew: true,
    };

    setEditEvent((prev) => ({
      ...prev,
      timingLaps: [...(prev.timingLaps || []), newLap],
    }));

    setEditLapName('');
    setEditLapTimingPoint('');
  };

  const handleReorderLaps = (reorderedLaps) => {
    setEditEvent((prev) => {
      if (!prev) return null;
      return { ...prev, timingLaps: reorderedLaps };
    });
  };

  const handleDeleteLap = (id) => {
    setEditEvent((prev) => ({
      ...prev,
      timingLaps: prev.timingLaps?.filter((lap) => lap.TimingId !== id),
    }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('LogoImage', file);
    setUploadProgress(true);
    try {
      const response = await fetch(`${URL}Event/uploadEventImage`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
          // Do NOT set Content-Type manually when using FormData with fetch
        },
        body: formData,
      });

      // 🔒 Handle 403 explicitly
      if (response.status === 403) {
        toast.error('Session expired. Please login again.');
        localStorage.setItem('adminAuthenticated', false);
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return;
      }

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      if (data.Status) {
        setEditEvent((prev) => ({ ...prev, logoUrl: data.Url }));
      }
    } catch (error) {
      console.error('Image upload failed:', error);
    } finally {
      setUploadProgress(false);
    }
  };

  const handleEditEvent = async (e) => {
    e.preventDefault();

    if (isWiclax && (!wiclaxResultsUrl || wiclaxChannels.length === 0)) {
      toast.error('Please enter the Wiclax Results Url and select at least one Wiclax Channel');
      return;
    }

    const payload = {
      EventInfoKey: event?.id || 0,
      EventName: editEvent?.name,
      StartTime: editEvent?.startTime,
      EndTime: editEvent?.endTime,
      LogoUrl: editEvent?.logoUrl,
      IsRekala: isRekla ? 1 : 0,
      LiveTrackStatus: liveTrackStatus ? 1 : 0, // Modify if this needs to be dynamic
      time_slabs: (editEvent?.timingLaps || []).map((lap, index) => ({
        TimingId: Number(lap.TimingId),
        TimeSlabName: lap.TimingName,
        DisplayOrder: index,
      })),
      IsWiclax: isWiclax ? 1 : 0,
      WiclaxEventName: editEvent?.name,
      WiclaxResultsUrl: wiclaxResultsUrl,
      WiclaxChannels: wiclaxChannels.join(','),
      WiclaxTimeField: wiclaxTimeField,
    };

    try {
      const response = await fetch(`${URL}Event/UpdateEventInfo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`, // Replace with your actual token
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      // 🔒 Handle 403 explicitly
      if (response.status === 403) {
        toast.error('Session expired. Please login again.');
        localStorage.setItem('adminAuthenticated', false);
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data);
      toast.success('Event updated successfully');
      setOpen(false);
      onEventUpdated?.();
    } catch (error) {
      console.error('API Error:', error);
      toast.error('Failed to update event');
    }
  };

  const handleWiclaxChange = (value) => {
    setIsWiclax(value);

    if (value) {
      setIsRekla(false);
      setWiclaxTimeField('chiptime');
    } else {
      setWiclaxResultsUrl('');
      setWiclaxChannels([]);
      setWiclaxTimeField('chiptime');
    }
  };

  const handleWiclaxChannelToggle = (channel) => {
    setWiclaxChannels((prev) => (prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]));
  };

  const performReklaChange = (value) => {
    setIsRekla(value);

    if (value) {
      setIsWiclax(false);
      setLiveTrackStatus(false);
      const startLap = {
        id: '0', // use index as ID
        TimingId: 11,
        TimingName: 'START',
      };

      const finishLap = {
        id: '1', // use next index as ID
        TimingId: 12,
        TimingName: 'FINISH',
      };

      setEditEvent((prev) => ({
        ...prev,
        timingLaps: [startLap, finishLap], // Replace all existing laps with only START and FINISH
      }));

      setEditLapName('');
      setEditLapTimingPoint('');
    } else {
      setEditEvent((prev) => ({
        ...prev,
        timingLaps: prev.timingLaps.filter((lap) => lap.TimingId !== 11 && lap.TimingId !== 12),
      }));
    }
  };

  const handleConfirmReklaConversion = () => {
    setShowReklaConfirmDialog(false);
    performReklaChange(true);
  };

  const handleCancelReklaConversion = () => {
    setShowReklaConfirmDialog(false);
    // Keep the switch in its previous state
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: { width: { lg: '40%', md: '50%', xs: '96%' }, maxWidth: '95%' }, // Custom narrow width
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Edit Event Info
        <IconButton
          aria-label="close"
          onClick={() => setOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Event Name"
                name="name"
                value={editEvent?.name || ''}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={6}>
                <DateTimePicker
                  label="Start Time"
                  value={editEvent?.startTime ? new Date(editEvent.startTime) : null}
                  onChange={(newValue) =>
                    handleInputChange({
                      target: {
                        name: 'startTime',
                        value: format(newValue, "yyyy-MM-dd'T'HH:mm:ss"),
                      },
                    })
                  }
                  ampm
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </Grid>

              <Grid item xs={6}>
                <DateTimePicker
                  label="End Time"
                  value={editEvent?.endTime ? new Date(editEvent.endTime) : null}
                  onChange={(newValue) =>
                    handleInputChange({
                      target: {
                        name: 'endTime',
                        value: format(newValue, "yyyy-MM-dd'T'HH:mm:ss"),
                      },
                    })
                  }
                  ampm
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </Grid>
            </LocalizationProvider>

            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={liveTrackStatus}
                    onChange={(e) => setLiveTrackStatus(e.target.checked)}
                    color="primary"
                  />
                }
                label="Live Track Status"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isRekla}
                    onChange={(e) =>
                      !isPreviousValueIsRekla && !isRekla
                        ? setShowReklaConfirmDialog(true)
                        : performReklaChange(e.target.checked)
                    }
                    color="primary"
                    disabled={isWiclax}
                  />
                }
                label="Rekla"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isWiclax}
                    onChange={(e) => handleWiclaxChange(e.target.checked)}
                    color="primary"
                    disabled={isRekla}
                  />
                }
                label="Is Wiclax"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Wiclax Results Url"
                value={wiclaxResultsUrl}
                onChange={(e) => setWiclaxResultsUrl(e.target.value)}
                required={isWiclax}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Wiclax Channels</Typography>
              <FormGroup row>
                {WICLAX_CHANNEL_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={wiclaxChannels.includes(option.value)}
                        onChange={() => handleWiclaxChannelToggle(option.value)}
                      />
                    }
                    label={option.label}
                  />
                ))}
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormLabel component="legend">Wiclax Timing</FormLabel>
              <RadioGroup row value={wiclaxTimeField} onChange={(e) => setWiclaxTimeField(e.target.value)}>
                <FormControlLabel value="chiptime" control={<Radio />} label="Chip Time" />
                <FormControlLabel value="time" control={<Radio />} label="Timing" />
              </RadioGroup>
            </Grid>

            <Grid item xs={12} sx={isRekla ? { display: 'none' } : {}}>
              <Typography variant="subtitle1">Event Logo</Typography>

              <Button
                variant="outlined"
                component="label"
                sx={{
                  mt: 1,
                  mr: 2,
                  color: '#9f1d21',
                  borderColor: '#9f1d21',
                  '&:hover': {
                    borderColor: '#9f1d21',
                    backgroundColor: 'rgba(240, 78, 152, 0.08)', // optional hover effect
                  },
                }}
                disabled={uploadProgress}
              >
                Upload Logo
                <input type="file" accept="image/*" hidden onChange={handleLogoUpload} />
              </Button>

              {editEvent?.logoUrl && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setEditEvent((prev) => ({ ...prev, logoUrl: '' }))}
                    sx={{ mt: 1 }}
                  >
                    Remove Logo
                  </Button>

                  <Box mt={2}>
                    <img src={editEvent?.logoUrl} alt="Event Logo" style={{ height: 100 }} />
                  </Box>
                </>
              )}
            </Grid>

            {/* Lap Inputs */}
            <Grid item xs={6} sx={isRekla ? { display: 'none' } : {}}>
              <TextField
                label="Mat ID"
                value={editLapName}
                onChange={(e) => setEditLapName(e.target.value)}
                fullWidth
                placeholder="Mat ID"
              />
            </Grid>
            <Grid item xs={6} sx={isRekla ? { display: 'none' } : {}}>
              <TextField
                label="Timing Point Name"
                value={editLapTimingPoint}
                onChange={(e) => setEditLapTimingPoint(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sx={isRekla ? { display: 'none' } : {}}>
              <Button
                variant="outlined"
                onClick={handleAddLap}
                fullWidth
                sx={{
                  color: '#9f1d21',
                  borderColor: '#9f1d21',
                  '&:hover': {
                    backgroundColor: '#fff0f5',
                    borderColor: '#e3272c',
                  },
                }}
              >
                Add Lap
              </Button>
            </Grid>

            {/* Drag-and-drop laps section (placeholder) */}
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                (Drag and reorder laps here)
              </Typography>
              {/* Replace this with your actual drag-and-drop component */}
              {/* <p>{JSON.stringify(editEvent?.timingLaps)}</p> */}
              <div className="pt-4">
                <DragAndMoveExample
                  isRekla={isRekla}
                  timingLaps={editEvent?.timingLaps || []}
                  onReorder={handleReorderLaps}
                  onDelete={handleDeleteLap}
                  onClose={() => {
                    setOpen(false);
                    onEventUpdated?.();
                  }}
                />
              </div>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                onClick={() => setOpen(false)}
                sx={{
                  color: '#9f1d21',
                  borderColor: '#9f1d21',
                  '&:hover': {
                    backgroundColor: '#fff0f5',
                    borderColor: '#e3272c',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                sx={{
                  backgroundColor: '#9f1d21',
                  '&:hover': {
                    backgroundColor: '#e3272c',
                  },
                }}
                onClick={handleEditEvent} // ✅ correct event handler
              >
                Save Changes
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {/* Rekla Conversion Confirmation Dialog */}
      <Dialog
        open={showReklaConfirmDialog}
        onClose={handleCancelReklaConversion}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                backgroundColor: '#fff3cd',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem' }}>⚠️</Typography>
            </Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Convert to Rekla Event?
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            Converting this event to a Rekla event will:
          </Typography>

          <Box sx={{ pl: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
              <span style={{ marginRight: 8, color: '#d32f2f' }}>•</span>
              Remove all current timing lap data
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
              <span style={{ marginRight: 8, color: '#d32f2f' }}>•</span>
              Replace with only START and FINISH laps
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
              <span style={{ marginRight: 8, color: '#d32f2f' }}>•</span>
              This action cannot be undone
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: 1,
              p: 2,
              mt: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#856404' }}>
              ⚠️ Warning: All existing timing lap configurations will be permanently lost.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCancelReklaConversion}
            variant="outlined"
            sx={{
              color: '#6c757d',
              borderColor: '#6c757d',
              '&:hover': {
                backgroundColor: '#f8f9fa',
                borderColor: '#5a6268',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReklaConversion}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#c62828',
              },
            }}
          >
            Yes, Convert to Rekla
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

const ListEventFilters = () => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      {/* <Button
                variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={<FilterListIcon />}
                sx={{
                    color: '#9f1d21',
                    borderColor: '#9f1d21',
                    '&:hover': {
                        borderColor: '#e3272c',
                        backgroundColor: '#fff0f5'
                    }
                }}
            >
                Filters {selectedMonth || selectedYear ? '(Active)' : ''}
            </Button> */}
      {/* <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box sx={{ p: 2, width: 300 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
                    <div className="flex flex-col gap-4">
                        <TextField
                            select
                            label="Month"
                            value={selectedMonth || ''}
                            onChange={(e) => setSelectedMonth(e.target.value || null)}
                            size="medium"
                            fullWidth
                        >
                            <MenuItem value="">All Months</MenuItem>
                            {[...Array(12)].map((_, index) => (
                                <MenuItem key={index + 1} value={index + 1}>
                                    {new Date(2000, index).toLocaleString('default', { month: 'long' })}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Year"
                            value={selectedYear || ''}
                            onChange={(e) => setSelectedYear(e.target.value || null)}
                            size="medium"
                            fullWidth
                        >
                            <MenuItem value="">All Years</MenuItem>
                            {[...Array(10)].map((_, index) => {
                                const year = new Date().getFullYear() - 5 + index;
                                return (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                        <div className="flex justify-between mt-2">
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setSelectedMonth(null);
                                    setSelectedYear(null);
                                    setAnchorEl(null);
                                }}
                                sx={{
                                    color: '#9f1d21',
                                    borderColor: '#9f1d21',
                                    '&:hover': {
                                        borderColor: '#e3272c',
                                        backgroundColor: '#fff0f5'
                                    }
                                }}
                            >
                                Clear Filters
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => setAnchorEl(null)}
                                sx={{
                                    backgroundColor: '#9f1d21',
                                    '&:hover': {
                                        backgroundColor: '#e3272c'
                                    }
                                }}
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                </Box>
            </Popover> */}
    </>
  );
};

const DragAndMoveExample = ({ timingLaps, onReorder, onDelete, isCreate = false, onClose, isRekla }) => {
  const [items, setItems] = useState(timingLaps);

  const handleUpdate = (updatedItem) => {
    onClose?.();
    const updatedItems = items.map((item) => (item.TimingId === updatedItem.TimingId ? updatedItem : item));
    setItems(updatedItems);
  };

  // Update local state when props change
  useEffect(() => {
    setItems(timingLaps);
  }, [timingLaps]);

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    setItems(newItems);
    onReorder(newItems);
  };

  if (items.length === 0) {
    return (
      <div className="p-0 m-0">
        <h4 className="font-medium mb-3 text-sm text-gray-700">Drag to Reorder Timing Points</h4>
        <div className="p-8 text-center border-2 border-dashed border-gray-200 rounded-lg">
          <p className="text-sm text-gray-500">No timing laps added yet. Add some laps above to reorder them here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-0 m-0">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="modal-example-list">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`space-y-2 rounded-lg transition-colors min-h-[200px] ${
                snapshot.isDraggingOver ? 'bg-blue-50' : 'bg-transparent'
              }`}
              style={{
                // Force the container to have proper positioning context
                position: 'relative',
                zIndex: 1,
              }}
            >
              {items?.map((item, index) => (
                <Draggable key={item.TimingId.toString()} draggableId={item.TimingId.toString()} index={index}>
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`bg-blue-100 border-blue-200 ${
                        snapshot.isDragging ? 'shadow-xl scale-105 rotate-1 z-50' : 'hover:shadow-md'
                      } transition-all duration-200 cursor-default border-2`}
                      style={{
                        ...provided.draggableProps.style,
                        // Ensure proper z-index for dragging items in modal
                        zIndex: snapshot.isDragging ? 9999 : 1,
                      }}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center gap-3">
                          <div
                            {...provided.dragHandleProps}
                            className="cursor-grab active:cursor-grabbing p-1 hover:bg-white/50 rounded transition-colors flex-shrink-0"
                          >
                            <GripVertical className="h-4 w-4 text-gray-500" />
                          </div>
                          <div className="text-xs text-gray-500 font-mono flex-shrink-0">#{index + 1}</div>

                          <div className="flex-1 flex justify-between items-center gap-4">
                            <h3 className="font-medium text-gray-800 text-sm">{item.TimingId || item.name}</h3>
                            <p className="text-xs text-gray-600 mt-1 truncate">{item.timingPoint || item.TimingName}</p>
                            <div className="flex items-center gap-2">
                              {(!isCreate || item.isRekla) && (
                                <EditTimingDialog item={item} isRekla={isRekla} onUpdate={handleUpdate} />
                              )}
                              {!item.isRekla && (
                                <button
                                  className="text-red-500 text-xs border border-red-300 rounded px-2 py-1 hover:bg-red-50"
                                  style={{ cursor: 'pointer' }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onDelete(item.TimingId);
                                  }}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

function cleanAndCapitalize(text) {
  if (typeof text !== 'string') {
    return '';
  }

  return text
    .replace(/_/g, ' ') // Replace underscores with spaces
    .split(' ') // Split into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
    .join(' '); // Join words back to a sentence
}

// const CreateEventInfo = ({ open, setOpen }) => {
//     const token = localStorage.getItem('access_token');
//     // New state for managing timing laps in the form
//     const [lapName, setLapName] = useState('');
//     const [lapTimingPoint, setLapTimingPoint] = useState('');
//     const [editEvent, setEditEvent] = useState(null);

//     const [newEvent, setNewEvent] = useState({
//         name: '',
//         startTime: '',
//         endTime: '',
//         timingDetails: '',
//         logo: '',
//         status: 'not_started',
//         timingLaps: [],
//     });

//     const handleCreateEvent = async (e) => {
//         e.preventDefault();

//         if (!newEvent.name || !newEvent.startTime || !newEvent.endTime) {
//             toast.error('Please fill in all required fields');
//             return;
//         }

//         const apiBody = {
//             EventName: newEvent.name,
//             StartTime: new Date(newEvent.startTime).toISOString(),
//             EndTime: new Date(newEvent.endTime).toISOString(),
//             LogoUrl: newEvent.logo || '',
//             LiveTrackStatus: true,
//             time_slabs: newEvent.timingLaps.map((lap, index) => ({
//                 TimingId: lap.id || `lap-${index + 1}`,
//                 TimeSlabName: lap.name,
//                 DisplayOrder: index,
//             })),
//         };

//         try {
//             const res = await fetch('https://api-myrace-dev.coitor.com/Event/CreateEventInfo', {
//                 method: 'POST',
//                 headers: {
//                     'Authorization': `Bearer ${token}`,
//                     'Content-Type': 'application/json',
//                     'accept': 'application/json',
//                 },
//                 body: JSON.stringify(apiBody),
//             });

//             if (!res.ok) {
//                 const errorData = await res.json();
//                 toast.error(`Failed: ${errorData.message || res.statusText}`);
//                 return;
//             }

//             const data = await res.json();
//             toast.success('Event created successfully!');

//             // Reset form
//             setNewEvent({
//                 name: '',
//                 startTime: '',
//                 endTime: '',
//                 timingDetails: '',
//                 logo: '',
//                 status: 'not_started',
//                 timingLaps: [],
//             });
//             setOpen(false);
//         } catch (error) {
//             toast.error('Something went wrong. Try again.');
//             console.error(error);
//         }
//     };

//     const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, isEdit = false) => {
//         const { name, value } = e.target;

//         if (isEdit && editEvent) {
//             setEditEvent((prev) => prev ? { ...prev, [name]: value } : null);
//         } else {
//             setNewEvent((prev) => ({ ...prev, [name]: value }));
//         }
//     };

//     const handleAddLap = (isEdit = false) => {
//         if (isEdit && editEvent) {
//             if (!editLapName || !editLapTimingPoint) {
//                 toast.error('Please fill in all lap fields');
//                 return;
//             }

//             const newLap = {
//                 id: Date.now().toString(),
//                 name: editLapName,
//                 timingPoint: editLapTimingPoint
//             };

//             setEditEvent((prev) => {
//                 if (!prev) return null;
//                 const updatedLaps = [...(prev.timingLaps || []), newLap];
//                 return { ...prev, timingLaps: updatedLaps };
//             });

//             // Reset input fields
//             setEditLapName('');
//             setEditLapTimingPoint('');
//         } else {
//             if (!lapName || !lapTimingPoint) {
//                 toast.error('Please fill in all lap fields');
//                 return;
//             }

//             const newLap = {
//                 id: Date.now().toString(),
//                 name: lapName,
//                 timingPoint: lapTimingPoint
//             };

//             setNewEvent((prev) => {
//                 const updatedLaps = [...(prev.timingLaps || []), newLap];
//                 return { ...prev, timingLaps: updatedLaps };
//             });

//             // Reset input fields
//             setLapName('');
//             setLapTimingPoint('');
//         }
//     };

//     return (
//         <>
//             <div onClick={() => setOpen(true)} className="flex justify-end mb-4">
//                 <Button className="bg-blue-600 hover:bg-blue-700">Create New Event</Button>
//             </div>
//             <Dialog open={open} onOpenChange={setOpen}>
//                 <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
//                     {/* <DialogHeader>
//                     <DialogTitle>Create New Event</DialogTitle>
//                 </DialogHeader> */}
//                     <div className="overflow-y-auto max-h-[80vh] pr-2">
//                         <form onSubmit={handleCreateEvent} className="space-y-4 mt-4 px-1">
//                             <div className="space-y-2">
//                                 <label htmlFor="name" className="block text-sm font-medium">
//                                     Event Name
//                                 </label>
//                                 <Input
//                                     id="name"
//                                     name="name"
//                                     value={newEvent.name}
//                                     onChange={handleInputChange}
//                                     placeholder="e.g., City Marathon 2025"
//                                     required
//                                 />
//                             </div>

//                             <div className="space-y-2">
//                                 <label htmlFor="startTime" className="block text-sm font-medium">
//                                     Start Time
//                                 </label>
//                                 <Input
//                                     id="startTime"
//                                     name="startTime"
//                                     type="datetime-local"
//                                     value={newEvent.startTime}
//                                     onChange={handleInputChange}
//                                     required
//                                 />
//                             </div>

//                             <div className="space-y-2">
//                                 <label htmlFor="endTime" className="block text-sm font-medium">
//                                     End Time
//                                 </label>
//                                 <Input
//                                     id="endTime"
//                                     name="endTime"
//                                     type="datetime-local"
//                                     value={newEvent.endTime}
//                                     onChange={handleInputChange}
//                                     required
//                                 />
//                             </div>

//                             <div className="space-y-2">
//                                 <label htmlFor="logo" className="block text-sm font-medium">
//                                     Event Logo URL
//                                 </label>
//                                 <Input
//                                     id="logo"
//                                     name="logo"
//                                     value={newEvent.logo}
//                                     onChange={handleInputChange}
//                                     placeholder="URL to event logo"
//                                 />
//                             </div>

//                             {/* Timing Laps Table */}
//                             <div className="space-y-2">
//                                 <label className="block text-sm font-medium">
//                                     Timing Laps
//                                 </label>

//                                 <div className="grid grid-cols-2 gap-2 mb-2">
//                                     <Input
//                                         placeholder="Lap Name (e.g., 5K)"
//                                         value={lapName}
//                                         onChange={(e) => setLapName(e.target.value)}
//                                     />
//                                     <Input
//                                         placeholder="Timing Point (e.g., 5 kilometers)"
//                                         value={lapTimingPoint}
//                                         onChange={(e) => setLapTimingPoint(e.target.value)}
//                                     />
//                                 </div>

//                                 <Button
//                                     type="button"
//                                     variant="outline"
//                                     className="w-full mb-2"
//                                     onClick={() => handleAddLap(false)}
//                                 >
//                                     Add Lap
//                                 </Button>

//                                 {/* {newEvent.timingLaps && newEvent.timingLaps.length > 0 ? (
//                         <Table>
//                           <TableHeader>
//                             <TableRow>
//                               <TableHead>ID</TableHead>
//                               <TableHead>Name</TableHead>
//                               <TableHead>Timing Point</TableHead>
//                               <TableHead className="text-right">Actions</TableHead>
//                             </TableRow>
//                           </TableHeader>
//                           <TableBody>
//                             {newEvent.timingLaps.map((lap, index) => (
//                               <TableRow key={lap.id}>
//                                 <TableCell>{index + 1}</TableCell>
//                                 <TableCell>{lap.name}</TableCell>
//                                 <TableCell>{lap.timingPoint}</TableCell>
//                                 <TableCell className="text-right">
//                                   <Button
//                                     variant="ghost"
//                                     size="sm"
//                                     className="text-destructive hover:text-destructive/80"
//                                     onClick={() => handleRemoveLap(lap.id, false)}
//                                   >
//                                     Remove
//                                   </Button>
//                                 </TableCell>
//                               </TableRow>
//                             ))}
//                           </TableBody>
//                         </Table>
//                       ) : (
//                         <p className="text-sm text-gray-500 text-center py-4 border rounded">
//                           No timing laps added. Add at least one lap.
//                         </p>
//                       )} */}
//                             </div>

//                             {/* Drag and Drop Example */}
//                             <div className="pt-4">
//                                 <DragAndMoveExample
//                                     timingLaps={newEvent.timingLaps || []}
//                                     onReorder={(reorderedLaps) => handleReorderLaps(reorderedLaps, false)}
//                                 />
//                             </div>

//                             <div className="flex justify-end pt-4 border-t">
//                                 <Button
//                                     type="button"
//                                     variant="outline"
//                                     onClick={() => setOpen(false)}
//                                     className="mr-2"
//                                 >
//                                     Cancel
//                                 </Button>
//                                 <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
//                                     Create Event
//                                 </Button>
//                             </div>
//                         </form>
//                     </div>
//                 </DialogContent>
//             </Dialog>
//         </>
//     )
// }
